import { ReactElement, useMemo, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { AxiosError } from 'axios'
import { find } from 'lodash-es'
import TabPanel from '@mui/lab/TabPanel'
import { Stack, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import Tab from '@mui/material/Tab'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import {
  useBlockScene,
  useElementLabel,
  useElementType,
  useResultsInvalidation,
} from '@editorHooks'
import {
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleDocumentationBoxSection,
  CollapsibleGeometryBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  CollapsibleSplitBoxSection,
  DocumentationItem,
} from '@editorComponents'
import { useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getVerticalTransmissionGraph, getModel, getProjectFiles } from '@queries'
import { createBeam, deleteBeam, updateBeam as updateBeamReq } from '@mutations'
import { buildErrorMessage, documentationRootGuid } from 'src/constants'
import { useElementHistory } from 'src/hooks'
import SingleElementCSForm from '../../../../../../components/SingleElementCSForm'
import CopyToOtherStoreys from '../../../MultiEdit/components/CopyToOtherStoreys'
import PlanSettingsForm from '../../../PlanSettingsForm'
import SplitLineElementForm from '../../../SplitLineElementForm'
import BeamFormBase from './BeamFormBase'
import GeometryForm from './GeometryForm'
import { beamTabs } from './tabs'

const StyledTabPanel = styled(TabPanel)({
  padding: '8px',
})

type BeamGeometryType = 'xy' | 'xy_diagonal'

interface ToggleProps {
  geometryType: BeamGeometryType
  setGeometryType: (type: BeamGeometryType) => void
}

const BeamGeometryTypeToggle = ({ geometryType, setGeometryType }: ToggleProps) => {
  return (
    <Stack direction="column" spacing={1} alignItems="center" sx={{ width: '100%' }}>
      <ToggleButtonGroup
        value={geometryType}
        exclusive
        onChange={(_, newValue) => newValue && setGeometryType(newValue)}
        aria-label="beam geometry type"
        fullWidth
        size="small"
      >
        <ToggleButton value="xy" aria-label="xy beam" fullWidth sx={{ paddingY: 0 }}>
          X oder Y
        </ToggleButton>
        <ToggleButton
          value="xy_diagonal"
          aria-label="xy diagonal beam"
          fullWidth
          sx={{ paddingY: 0 }}
        >
          X/Y Diagonal
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography>
        {geometryType === 'xy' ? 'z. Bsp. horizontaler Unterzug' : 'z. Bsp. xy diagonaler Unterzug'}
      </Typography>
    </Stack>
  )
}

interface Props {
  selectedElement: string
  onClose: () => void
  interactionMode: '2D' | '3D'
}

const BeamForm = ({ selectedElement, onClose, interactionMode }: Props): ReactElement | null => {
  const [geometryType, setGeometryType] = useState<BeamGeometryType>('xy')
  const elementType = useElementType(selectedElement)

  const { beams } = useModelStore(state => state.model)
  const removeBeam = useModelStore(state => state.removeBeam)
  const updateBeam = useModelStore(state => state.updateBeam)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()
  const client = useQueryClient()

  const invalidateResults = useResultsInvalidation()

  const getLabel = useElementLabel()

  const beam = useMemo(() => {
    return find(beams, ['guid', selectedElement]) as PlanLineElement
  }, [beams, selectedElement])

  const { setStale: setBeamStale } = useElementHistory({
    element: beam,
    getCurrentElement: () => {
      const beams = useModelStore.getState().model.beams
      return find(beams, ['guid', selectedElement]) as PlanLineElement
    },
    removeElement: beam => removeBeam(beam.guid),
    resetElement: beam => updateBeam(beam),
    dependencies: [selectedElement],
  })

  useEffect(() => {
    setTypeVisibility('beams' as ElementTypes, true)
  }, [setTypeVisibility])

  useStructuralPlanningFormEsc(() => {
    // beam has to be saved otherwise remove
    if (beam?.is_local) {
      removeBeam(beam.guid)
    }
    onClose()
  }, !!selectedElement)

  const { mutateAsync, isLoading } = useMutation(
    (beam: PlanLineElement) => createBeam.request(projectId, beam),
    {
      onSuccess: async (data: PlanLineElement) => {
        setBeamStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Unterzug erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Unterzugs'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: update, isLoading: isUpdating } = useMutation(
    (beam: PlanLineElement) => updateBeamReq.request(projectId, beam),
    {
      onSuccess: async (data: PlanLineElement) => {
        setBeamStale(data)
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Unterzug erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Unterzugs'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (beamGuid: string) => {
      return deleteBeam.request(projectId, beamGuid)
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getProjectFiles.getKey(projectId, documentationRootGuid)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Unterzug erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: Error | AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Unterzugs'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isLoading || isDeleting || isUpdating)

  if (!beam) return null

  if (beam.is_local) {
    return (
      <Stack direction="column" spacing={1} alignItems={'center'} p={1}>
        <GeometryForm
          beam={beam}
          interactionMode={interactionMode}
          handleSubmit={beam => mutateAsync(beam)}
          handleDelete={() => removeBeam(beam.guid)}
          geometryType={geometryType}
          geometryTypeToggle={
            <BeamGeometryTypeToggle geometryType={geometryType} setGeometryType={setGeometryType} />
          }
          isSubmitting={isLoading}
          showCreateLabel
        />
      </Stack>
    )
  }

  return (
    <BeamFormBase
      initialTabValue={beamTabs.geometry.tab}
      selectedElement={beam.guid}
      tab={
        <Tab value={beamTabs.geometry.tab} label={beamTabs.geometry.label} data-cy="tab-geometry" />
      }
    >
      <StyledTabPanel value={beamTabs.geometry.tab}>
        <Stack direction="column" spacing={1}>
          <StructuralPlanningElementFormHeader
            elementGuid={selectedElement}
            label={getLabel(selectedElement)}
            onClose={onClose}
          />
          <CollapsibleAssemblyBoxSection label="Querschnitt/Aufbau" defaultExpanded={true}>
            <SingleElementCSForm
              selectedElement={selectedElement}
              elementType={elementType as ElementTypes}
            />
          </CollapsibleAssemblyBoxSection>
          <CollapsibleGeometryBoxSection defaultExpanded={true}>
            <GeometryForm
              beam={beam}
              interactionMode={interactionMode}
              handleSubmit={beam => update(beam)}
              handleDelete={() => handleDelete(beam.guid)}
              isDeleting={isDeleting}
              geometryType={geometryType}
              geometryTypeToggle={
                <BeamGeometryTypeToggle
                  geometryType={geometryType}
                  setGeometryType={setGeometryType}
                />
              }
              isSubmitting={isUpdating}
            />
          </CollapsibleGeometryBoxSection>

          <CollapsibleSettingsBoxSection defaultExpanded={false}>
            <PlanSettingsForm element={beam} setStale={setBeamStale} />
          </CollapsibleSettingsBoxSection>

          <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
            <SplitLineElementForm
              elementGuid={selectedElement}
              elementType="beams"
              interactionMode={interactionMode}
              setElementStale={setBeamStale}
            />
          </CollapsibleSplitBoxSection>

          {beam.storey && (
            <CollapsibleCopyBoxSection label="Kopieren" defaultExpanded={false}>
              <CopyToOtherStoreys selectedElements={[selectedElement]} storey={beam.storey} />
            </CollapsibleCopyBoxSection>
          )}
          <CollapsibleDocumentationBoxSection defaultExpanded={false}>
            <DocumentationItem itemGuid={selectedElement} />
          </CollapsibleDocumentationBoxSection>
          <CollapsibleProblemsBoxSection elementGuid={selectedElement} defaultExpanded={true} />
        </Stack>
      </StyledTabPanel>
    </BeamFormBase>
  )
}

export default BeamForm
